import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    Switch,
    FormControlLabel,
    TextField,
    Radio,
    RadioGroup,
    FormControl,
    FormLabel,
    Select,
    MenuItem,
    Button,
    Alert,
    CircularProgress,
    Divider,
    Checkbox,
    FormGroup,
    Chip,
    Tooltip
} from '@mui/material';
import {
    getSystemConfig,
    updateSystemConfig,
    getSchedulerStatus,
    runScheduler,
    updateSchedulerConfig,
    resetSchedulerStats
} from '../../../services/api';

const AI_PROVIDER_OPTIONS = [
    {
        value: 'system-default',
        label: 'Padrao interno',
        description: 'Usa a regra tecnica atual do Mensure.'
    },
    {
        value: 'gemini-free',
        label: 'Gemini gratis',
        description: 'Usa o pool gratuito de chaves Gemini.'
    },
    {
        value: 'gemini-paid',
        label: 'Gemini pago',
        description: 'Usa a chave paga configurada no ambiente.'
    },
    {
        value: 'deepseek',
        label: 'DeepSeek',
        description: 'Tenta DeepSeek primeiro em tarefas de texto.'
    },
    {
        value: 'disabled',
        label: 'Desativado',
        description: 'Bloqueia uso de IA neste servico.'
    }
];

const GLOBAL_AI_SERVICES = [
    {
        key: 'domainEnrichment',
        label: 'Enriquecimento de dominio',
        scope: 'Global estrutural',
        description: 'Abrangencia, tipo, localidade e categorias da fonte. Nao e configuravel por marca.'
    },
    {
        key: 'smartScraper',
        label: 'Smart Scraper e rescrape',
        scope: 'Global estrutural',
        description: 'Extracao inteligente de conteudo e fallback com Gemini Vision.'
    },
    {
        key: 'visualMetrics',
        label: 'Metricas visuais',
        scope: 'Global estrutural',
        description: 'Leitura de screenshots, imagens e evidencias visuais.'
    },
    {
        key: 'ocr',
        label: 'OCR de impressos',
        scope: 'Global operacional',
        description: 'Extracao visual de jornais e revistas. A etapa com imagem usa Gemini Vision; DeepSeek so se aplica a tarefas de texto.'
    },
    {
        key: 'qualitativeAnalysis',
        label: 'Analise qualitativa',
        scope: 'Padrao herdavel',
        description: 'Padrao global herdado quando a marca nao tiver regra propria.'
    },
    {
        key: 'exposureAnalysis',
        label: 'Exposicao por IA',
        scope: 'Padrao herdavel',
        description: 'Padrao global para o botao rapido de exposicao por IA.'
    },
    {
        key: 'sentimentAnalysis',
        label: 'Sentimento por IA',
        scope: 'Padrao herdavel',
        description: 'Padrao global herdado para analise de sentimento assistida por IA.'
    },
    {
        key: 'briefingAssistant',
        label: 'Assistente de briefing',
        scope: 'Padrao herdavel',
        description: 'Padrao global para sugestoes de palavras-chave, produtos e descricoes.'
    }
];

const AFTER_SCRAPING_AI_SERVICES = [
    {
        key: 'qualitativeAnalysis',
        label: 'Analise qualitativa completa',
        description: 'Gera KPIA no artigo novo: ocupacao, exposicao, sentimento, mensagens, porta-voz e SmartScore.'
    },
    {
        key: 'exposureAnalysis',
        label: 'Exposicao por IA',
        description: 'Usa IA apenas para classificar exposicao. Ignorado quando a analise completa estiver ativa.'
    },
    {
        key: 'sentimentAnalysis',
        label: 'Sentimento por IA',
        description: 'Usa IA apenas para sentimento. Ignorado quando a analise completa estiver ativa.'
    }
];

// ═══════════════════════════════════════════════════════════════════
// MÓDULOS CONTRATADOS — Serviços que podem ser habilitados/desabilitados
// ═══════════════════════════════════════════════════════════════════
const CONTRACT_MODULES = [
    {
        key: 'smartScore',
        label: 'SmartScore',
        description: 'Pontuação editorial inteligente por artigo. Combina sentimento, exposição, Tier, alcance e outros indicadores em uma nota de -10 a +10.',
        icon: '📊',
        category: 'Editorial',
        defaultEnabled: true,
    },
    {
        key: 'aiQualitativeAnalysis',
        label: 'Análise Qualitativa IA',
        description: 'Análise completa por IA (KPIA): ocupação editorial, exposição, sentimento, mensagens-chave, porta-vozes e muito mais.',
        icon: '🧠',
        category: 'IA',
        defaultEnabled: true,
    },
    {
        key: 'shareOfVoice',
        label: 'Share of Voice',
        description: 'Gráficos e métricas comparando a visibilidade da marca com a dos concorrentes monitorados no dashboard.',
        icon: '📈',
        category: 'Dashboard',
        defaultEnabled: true,
    },
    {
        key: 'briefingAssistant',
        label: 'Assistente de Briefing IA',
        description: 'Sugestões automáticas de descrição, palavras-chave e produtos ao configurar uma marca.',
        icon: '✍️',
        category: 'IA',
        defaultEnabled: true,
    },
    {
        key: 'newsletter',
        label: 'Newsletter Editorial',
        description: 'Criação, personalização e envio automatizado de newsletters editoriais para contatos cadastrados.',
        icon: '📩',
        category: 'Comunicação',
        defaultEnabled: true,
    },
    {
        key: 'publicDashboard',
        label: 'Dashboard Compartilhado',
        description: 'Geração de links públicos para compartilhamento do dashboard com clientes e stakeholders externos.',
        icon: '🔗',
        category: 'Acesso',
        defaultEnabled: true,
    },
    {
        key: 'powerBi',
        label: 'Power BI Analytics',
        description: 'Integração com relatórios Microsoft Power BI incorporados diretamente nas páginas de marca.',
        icon: '📉',
        category: 'Analytics',
        defaultEnabled: false,
    },
];

const CATEGORY_COLORS = {
    'Editorial': '#7c3aed',
    'IA':        '#0369a1',
    'Dashboard': '#0891b2',
    'Comunicação': '#059669',
    'Acesso':    '#d97706',
    'Analytics': '#dc2626',
};

const getFeatureEnabled = (config, featureKey) => {
    const feat = config?.features?.[featureKey];
    if (!feat) return true; // default seguro
    if (typeof feat === 'object' && 'enabled' in feat) return feat.enabled;
    return Boolean(feat);
};

const setFeatureEnabled = (config, featureKey, enabled) => ({
    ...config,
    features: {
        ...(config.features || {}),
        [featureKey]: {
            ...(config.features?.[featureKey] || {}),
            enabled
        }
    }
});

const getAIServiceProvider = (config, serviceKey) => (
    config.aiServiceSettings?.services?.[serviceKey]?.provider || 'system-default'
);

const setAIServiceProvider = (config, serviceKey, provider) => ({
    ...config,
    aiServiceSettings: {
        ...config.aiServiceSettings,
        services: {
            ...(config.aiServiceSettings?.services || {}),
            [serviceKey]: {
                ...(config.aiServiceSettings?.services?.[serviceKey] || {}),
                provider
            }
        }
    }
});

const getAfterScrapingEnabled = (config, serviceKey) => (
    config.aiApplicationPolicy?.afterScraping?.[serviceKey]?.enabled === true
);

const setAfterScrapingEnabled = (config, serviceKey, enabled) => ({
    ...config,
    aiApplicationPolicy: {
        ...config.aiApplicationPolicy,
        afterScraping: {
            ...(config.aiApplicationPolicy?.afterScraping || {}),
            [serviceKey]: {
                ...(config.aiApplicationPolicy?.afterScraping?.[serviceKey] || {}),
                enabled
            }
        }
    }
});

const setAIApplicationLimit = (config, groupKey, field, value) => ({
    ...config,
    aiApplicationLimits: {
        ...(config.aiApplicationLimits || {}),
        [groupKey]: {
            ...(config.aiApplicationLimits?.[groupKey] || {}),
            [field]: value
        }
    }
});

const SystemConfigPanel = () => {
    const [config, setConfig] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    // Scheduler state
    const [schedulerStatus, setSchedulerStatus] = useState(null);
    const [schedulerLoading, setSchedulerLoading] = useState(false);
    const [schedulerRunning, setSchedulerRunning] = useState(false);

    // Carregar configurações
    useEffect(() => {
        loadConfig();
        loadSchedulerStatus();

        // Atualizar status do scheduler a cada 10 segundos
        const interval = setInterval(loadSchedulerStatus, 10000);
        return () => clearInterval(interval);
    }, []);

    const loadConfig = async () => {
        try {
            setLoading(true);
            const response = await getSystemConfig();
            setConfig(response.data.data);
            setError(null);
        } catch (err) {
            setError('Erro ao carregar configurações: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const loadSchedulerStatus = async () => {
        try {
            const response = await getSchedulerStatus();
            setSchedulerStatus(response.data);
        } catch (err) {
            console.error('Erro ao carregar status do scheduler:', err);
        }
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            setSuccess(false);
            await updateSystemConfig(config);
            setSuccess(true);
            setError(null);
            setTimeout(() => setSuccess(false), 3000);
        } catch (err) {
            setError('Erro ao salvar configurações: ' + err.message);
        } finally {
            setSaving(false);
        }
    };

    const handleSaveScheduler = async () => {
        try {
            setSaving(true);
            setSuccess(false);
            await updateSchedulerConfig(config.enrichmentScheduler);
            await loadSchedulerStatus();
            setSuccess(true);
            setError(null);
            setTimeout(() => setSuccess(false), 3000);
        } catch (err) {
            setError('Erro ao salvar configurações do scheduler: ' + err.message);
        } finally {
            setSaving(false);
        }
    };

    const handleRunScheduler = async () => {
        try {
            setSchedulerRunning(true);
            setError(null);
            const response = await runScheduler();
            await loadSchedulerStatus();
            setSuccess(`Scheduler executado! Processados: ${response.data.processed}, Sucessos: ${response.data.succeeded}, Falhas: ${response.data.failed}`);
            setTimeout(() => setSuccess(false), 5000);
        } catch (err) {
            setError('Erro ao executar scheduler: ' + err.message);
        } finally {
            setSchedulerRunning(false);
        }
    };

    const handleResetStats = async () => {
        try {
            setSchedulerLoading(true);
            await resetSchedulerStats();
            await loadSchedulerStatus();
            setSuccess('Estatísticas resetadas com sucesso!');
            setTimeout(() => setSuccess(false), 3000);
        } catch (err) {
            setError('Erro ao resetar estatísticas: ' + err.message);
        } finally {
            setSchedulerLoading(false);
        }
    };

    const senderProfiles = config?.emailDelivery?.senderProfiles || [];
    const updateEmailDelivery = (updates) => {
        setConfig({
            ...config,
            emailDelivery: {
                defaultSenderKey: config.emailDelivery?.defaultSenderKey || 'env-default',
                senderProfiles,
                ...updates
            }
        });
    };

    const handleAddSenderProfile = () => {
        const key = `smtp-${Date.now()}`;
        updateEmailDelivery({
            defaultSenderKey: senderProfiles.length === 0 ? key : (config.emailDelivery?.defaultSenderKey || 'env-default'),
            senderProfiles: [
                ...senderProfiles,
                {
                    key,
                    label: 'Novo remetente',
                    fromName: 'Mensure Monitor',
                    email: '',
                    smtpHost: 'smtp.hostinger.com',
                    smtpPort: 465,
                    smtpSecure: true,
                    username: '',
                    password: '',
                    isActive: true
                }
            ]
        });
    };

    const handleSenderProfileChange = (key, field, value) => {
        updateEmailDelivery({
            senderProfiles: senderProfiles.map(profile => (
                profile.key === key ? { ...profile, [field]: value } : profile
            ))
        });
    };

    const handleRemoveSenderProfile = (key) => {
        const nextProfiles = senderProfiles.filter(profile => profile.key !== key);
        const currentDefault = config.emailDelivery?.defaultSenderKey;
        updateEmailDelivery({
            defaultSenderKey: currentDefault === key ? (nextProfiles[0]?.key || 'env-default') : currentDefault,
            senderProfiles: nextProfiles
        });
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress />
            </Box>
        );
    }

    if (!config) {
        return (
            <Alert severity="error">
                Erro ao carregar configurações
            </Alert>
        );
    }

    return (
        <Box>
            <Typography variant="h5" gutterBottom>
                Configurações do Sistema
            </Typography>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {success && (
                <Alert severity="success" sx={{ mb: 2 }}>
                    Configurações salvas com sucesso!
                </Alert>
            )}

            
            {/* ═══════════════════════════════════════════════════════════════════ */}
            {/* MÓDULOS CONTRATADOS — Serviços que podem ser habilitados/desabilitados */}
            {/* ═══════════════════════════════════════════════════════════════════ */}
            <Paper sx={{ p: 3, mb: 3, border: '1px solid', borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 1 }}>
                    <Box>
                        <Typography variant="h6">Remetentes de Newsletter</Typography>
                        <Typography variant="body2" color="text.secondary">
                            Perfil SMTP global usado por todos os envios de newsletter.
                        </Typography>
                    </Box>
                    <Button variant="outlined" onClick={handleAddSenderProfile}>
                        Adicionar remetente
                    </Button>
                </Box>
                <Divider sx={{ mb: 2 }} />

                <Alert severity="info" sx={{ mb: 2 }}>
                    Se nenhum perfil estiver ativo, o sistema usa as variaveis SMTP atuais do ambiente.
                </Alert>

                <FormControl fullWidth sx={{ mb: 2 }}>
                    <FormLabel sx={{ mb: 1 }}>Remetente padrao global</FormLabel>
                    <Select
                        value={config.emailDelivery?.defaultSenderKey || 'env-default'}
                        onChange={(e) => updateEmailDelivery({ defaultSenderKey: e.target.value })}
                    >
                        <MenuItem value="env-default">Variaveis do ambiente</MenuItem>
                        {senderProfiles.map(profile => (
                            <MenuItem key={profile.key} value={profile.key}>
                                {profile.label || profile.email || profile.key}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {senderProfiles.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                        Nenhum remetente cadastrado.
                    </Typography>
                ) : (
                    <Box sx={{ display: 'grid', gap: 2 }}>
                        {senderProfiles.map(profile => (
                            <Paper key={profile.key} variant="outlined" sx={{ p: 2 }}>
                                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                                    <TextField label="Nome do perfil" value={profile.label || ''} onChange={(e) => handleSenderProfileChange(profile.key, 'label', e.target.value)} />
                                    <TextField label="Nome exibido no remetente" value={profile.fromName || ''} onChange={(e) => handleSenderProfileChange(profile.key, 'fromName', e.target.value)} />
                                    <TextField label="Email remetente" value={profile.email || ''} onChange={(e) => handleSenderProfileChange(profile.key, 'email', e.target.value)} />
                                    <TextField label="Usuario SMTP" value={profile.username || ''} onChange={(e) => handleSenderProfileChange(profile.key, 'username', e.target.value)} placeholder="Se vazio, usa o email remetente" />
                                    <TextField label="Senha SMTP" type="password" value={profile.password || ''} onChange={(e) => handleSenderProfileChange(profile.key, 'password', e.target.value)} />
                                    <TextField label="Host SMTP" value={profile.smtpHost || ''} onChange={(e) => handleSenderProfileChange(profile.key, 'smtpHost', e.target.value)} />
                                    <TextField label="Porta" type="number" value={profile.smtpPort || 465} onChange={(e) => handleSenderProfileChange(profile.key, 'smtpPort', Number(e.target.value))} />
                                    <FormControlLabel control={<Switch checked={profile.smtpSecure !== false} onChange={(e) => handleSenderProfileChange(profile.key, 'smtpSecure', e.target.checked)} />} label="SSL/TLS" />
                                    <FormControlLabel control={<Switch checked={profile.isActive !== false} onChange={(e) => handleSenderProfileChange(profile.key, 'isActive', e.target.checked)} />} label="Ativo" />
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                                    <Chip
                                        size="small"
                                        color={(config.emailDelivery?.defaultSenderKey || 'env-default') === profile.key ? 'primary' : 'default'}
                                        label={(config.emailDelivery?.defaultSenderKey || 'env-default') === profile.key ? 'Padrao global' : 'Disponivel'}
                                    />
                                    <Button color="error" onClick={() => handleRemoveSenderProfile(profile.key)}>
                                        Remover
                                    </Button>
                                </Box>
                            </Paper>
                        ))}
                    </Box>
                )}
            </Paper>

            <Paper sx={{ p: 3, mb: 3, border: '1px solid', borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="h6">
                        🧩 Módulos &amp; Serviços Contratados
                    </Typography>
                    <Chip
                        label={`${CONTRACT_MODULES.filter(m => getFeatureEnabled(config, m.key)).length} / ${CONTRACT_MODULES.length} ativos`}
                        size="small"
                        color="primary"
                        variant="outlined"
                    />
                </Box>
                <Divider sx={{ mb: 2 }} />

                <Alert severity="info" sx={{ mb: 3 }}>
                    Controle quais soluções estão disponíveis para os clientes. Desabilitar um módulo oculta seus dados e bloqueia o acesso em toda a plataforma, sem apagar informações existentes.
                </Alert>

                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 2 }}>
                    {CONTRACT_MODULES.map(module => {
                        const enabled = getFeatureEnabled(config, module.key);
                        const catColor = CATEGORY_COLORS[module.category] || '#607D8B';
                        return (
                            <Paper key={module.key} variant="outlined" sx={{ p: 2, borderRadius: 2, borderColor: enabled ? catColor + '55' : 'divider', bgcolor: enabled ? catColor + '08' : 'action.hover', transition: 'all 0.2s', opacity: enabled ? 1 : 0.65 }}>
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                                            <Typography sx={{ fontSize: '1.1rem', lineHeight: 1 }}>{module.icon}</Typography>
                                            <Typography sx={{ fontWeight: 800, fontSize: '0.9rem' }}>{module.label}</Typography>
                                            <Chip label={module.category} size="small" sx={{ height: 16, fontSize: '0.6rem', fontWeight: 700, bgcolor: catColor + '20', color: catColor, border: '1px solid ' + catColor + '40' }} />
                                        </Box>
                                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.78rem', lineHeight: 1.4 }}>{module.description}</Typography>
                                    </Box>
                                    <Tooltip title={enabled ? 'Desabilitar módulo' : 'Habilitar módulo'} arrow>
                                        <Switch checked={enabled} onChange={(e) => setConfig(setFeatureEnabled(config, module.key, e.target.checked))} size="small" sx={{ flexShrink: 0 }} />
                                    </Tooltip>
                                </Box>
                                <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: enabled ? '#22c55e' : '#94a3b8' }} />
                                    <Typography variant="caption" sx={{ color: enabled ? '#22c55e' : 'text.disabled', fontWeight: 700 }}>{enabled ? 'Ativo' : 'Desabilitado'}</Typography>
                                </Box>
                            </Paper>
                        );
                    })}
                </Box>
            </Paper>

            <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                    Auto-Enriquecimento de Domínios
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <FormControlLabel
                    control={
                        <Switch
                            checked={config.autoEnrichNewDomains}
                            onChange={(e) => setConfig({ ...config, autoEnrichNewDomains: e.target.checked })}
                        />
                    }
                    label="Auto-enriquecer domínios novos"
                />

                <Alert severity="info" sx={{ mt: 2, mb: 3 }}>
                    Quando ativado, o sistema automaticamente busca audiência e enriquece domínios novos com IA ao detectá-los durante o scraping.
                </Alert>

                {config.autoEnrichNewDomains && (
                    <>
                        <Typography variant="subtitle1" gutterBottom sx={{ mt: 3 }}>
                            Serviços Habilitados
                        </Typography>

                        <FormGroup>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={config.enrichmentServices?.similarWeb ?? true}
                                        onChange={(e) => setConfig({
                                            ...config,
                                            enrichmentServices: {
                                                ...config.enrichmentServices,
                                                similarWeb: e.target.checked
                                            }
                                        })}
                                    />
                                }
                                label="SimilarWeb (buscar dados de audiência)"
                            />

                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={config.enrichmentServices?.geminiAI ?? true}
                                        onChange={(e) => setConfig({
                                            ...config,
                                            enrichmentServices: {
                                                ...config.enrichmentServices,
                                                geminiAI: e.target.checked
                                            }
                                        })}
                                    />
                                }
                                label="Gemini AI (enriquecer dados contextuais)"
                            />

                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={config.enrichmentServices?.fallbackEstimate ?? true}
                                        onChange={(e) => setConfig({
                                            ...config,
                                            enrichmentServices: {
                                                ...config.enrichmentServices,
                                                fallbackEstimate: e.target.checked
                                            }
                                        })}
                                    />
                                }
                                label="Fallback (usar estimativa se outros falharem)"
                            />
                        </FormGroup>

                        <Typography variant="subtitle1" gutterBottom sx={{ mt: 3 }}>
                            Aprovação Automática
                        </Typography>

                        <FormControl component="fieldset">
                            <RadioGroup
                                value={config.approvalMode || 'threshold'}
                                onChange={(e) => setConfig({ ...config, approvalMode: e.target.value })}
                            >
                                <FormControlLabel
                                    value="always-auto"
                                    control={<Radio />}
                                    label="Sempre aprovar automaticamente"
                                />
                                <FormControlLabel
                                    value="threshold"
                                    control={<Radio />}
                                    label="Aprovar se audiência acima do threshold"
                                />
                                <FormControlLabel
                                    value="always-manual"
                                    control={<Radio />}
                                    label="Sempre requerer revisão manual"
                                />
                            </RadioGroup>
                        </FormControl>

                        {config.approvalMode === 'threshold' && (
                            <TextField
                                label="Threshold de Audiência (visitas/mês)"
                                type="number"
                                value={config.autoApproveThreshold || 10000}
                                onChange={(e) => setConfig({ ...config, autoApproveThreshold: parseInt(e.target.value) })}
                                fullWidth
                                sx={{ mt: 2 }}
                                helperText="Domínios com audiência acima deste valor serão aprovados automaticamente"
                            />
                        )}
                    </>
                )}
            </Paper>

            <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                    IA e Servicos Mensure
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <Alert severity="info" sx={{ mb: 3 }}>
                    Estes sao os defaults globais do sistema. Servicos estruturais, como dominio, scraper e metricas visuais, ficam apenas aqui para manter a base consistente para todos os clientes.
                </Alert>

                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 3 }}>
                    <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            Padrao global
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Usado quando um servico estiver em "Padrao interno" e nenhuma marca tiver regra propria.
                        </Typography>
                    </Box>
                    <FormControl fullWidth size="small">
                        <Select
                            value={config.aiServiceSettings?.defaultProvider || 'gemini-free'}
                            onChange={(e) => setConfig({
                                ...config,
                                aiServiceSettings: {
                                    ...config.aiServiceSettings,
                                    defaultProvider: e.target.value
                                }
                            })}
                        >
                            {AI_PROVIDER_OPTIONS.map(option => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                    {GLOBAL_AI_SERVICES.map(service => {
                        const provider = getAIServiceProvider(config, service.key);
                        const providerInfo = AI_PROVIDER_OPTIONS.find(option => option.value === provider);

                        return (
                            <Paper key={service.key} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mb: 1 }}>
                                    <Box>
                                        <Typography sx={{ fontWeight: 800 }}>
                                            {service.label}
                                        </Typography>
                                        <Typography variant="caption" color="primary" sx={{ fontWeight: 700 }}>
                                            {service.scope}
                                        </Typography>
                                    </Box>
                                </Box>
                                <Typography variant="body2" color="text.secondary" sx={{ minHeight: 42, mb: 2 }}>
                                    {service.description}
                                </Typography>
                                <FormControl fullWidth size="small">
                                    <Select
                                        value={provider}
                                        onChange={(e) => setConfig(setAIServiceProvider(config, service.key, e.target.value))}
                                    >
                                        {AI_PROVIDER_OPTIONS.map(option => (
                                            <MenuItem key={option.value} value={option.value}>
                                                {option.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                    {providerInfo?.description}
                                </Typography>
                            </Paper>
                        );
                    })}
                </Box>

                <Divider sx={{ my: 3 }} />

                <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 0.5 }}>
                    Aplicacao automatica no scraping
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Define quais indicadores por IA serao gerados logo depois que um artigo novo for salvo pelo scraping.
                </Typography>

                <Alert severity="warning" sx={{ mb: 2 }}>
                    A analise qualitativa completa ja cobre exposicao e sentimento. Se ela estiver ativa, os dois atalhos abaixo ficam redundantes no processamento.
                </Alert>

                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2 }}>
                    {AFTER_SCRAPING_AI_SERVICES.map(service => (
                        <Paper key={service.key} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={getAfterScrapingEnabled(config, service.key)}
                                        onChange={(e) => setConfig(setAfterScrapingEnabled(config, service.key, e.target.checked))}
                                    />
                                }
                                label={
                                    <Typography sx={{ fontWeight: 800 }}>
                                        {service.label}
                                    </Typography>
                                }
                            />
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                {service.description}
                            </Typography>
                        </Paper>
                    ))}
                </Box>

                <Divider sx={{ my: 3 }} />

                <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 0.5 }}>
                    Limites de aplicacao manual
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Controla os limites usados quando o usuario dispara analises por IA manualmente em lote.
                </Typography>

                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                    <Typography sx={{ fontWeight: 800, mb: 1 }}>
                        KPIA em lote
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                        <TextField
                            label="Maximo de artigos por lote"
                            type="number"
                            value={config.aiApplicationLimits?.manualBatchKpia?.maxArticles ?? 50}
                            onChange={(e) => setConfig(setAIApplicationLimit(
                                config,
                                'manualBatchKpia',
                                'maxArticles',
                                Number(e.target.value)
                            ))}
                            inputProps={{ min: 1, max: 500 }}
                            helperText="Padrao anterior: 50 artigos"
                            fullWidth
                        />
                        <TextField
                            label="Intervalo entre artigos (ms)"
                            type="number"
                            value={config.aiApplicationLimits?.manualBatchKpia?.delayMs ?? 1000}
                            onChange={(e) => setConfig(setAIApplicationLimit(
                                config,
                                'manualBatchKpia',
                                'delayMs',
                                Number(e.target.value)
                            ))}
                            inputProps={{ min: 0, max: 10000, step: 250 }}
                            helperText="Padrao anterior: 1000 ms"
                            fullWidth
                        />
                    </Box>
                </Paper>
            </Paper>

            {/* SCHEDULER DE RE-ENRIQUECIMENTO */}
            <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                    📅 Scheduler de Re-Enriquecimento Automático
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <FormControlLabel
                    control={
                        <Switch
                            checked={config.enrichmentScheduler?.enabled || false}
                            onChange={(e) => setConfig({
                                ...config,
                                enrichmentScheduler: {
                                    ...config.enrichmentScheduler,
                                    enabled: e.target.checked
                                }
                            })}
                        />
                    }
                    label="Habilitar scheduler automático"
                />

                <Alert severity="info" sx={{ mt: 2, mb: 3 }}>
                    O scheduler re-enriquece automaticamente domínios baseado em prioridade e frequência de atualização.
                    Ele busca dados do SimilarWeb e Gemini AI periodicamente.
                </Alert>

                {config.enrichmentScheduler?.enabled && (
                    <>
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 3 }}>
                            <TextField
                                label="Intervalo entre execuções (minutos)"
                                type="number"
                                value={config.enrichmentScheduler?.intervalMinutes || 60}
                                onChange={(e) => setConfig({
                                    ...config,
                                    enrichmentScheduler: {
                                        ...config.enrichmentScheduler,
                                        intervalMinutes: parseInt(e.target.value)
                                    }
                                })}
                                inputProps={{ min: 5, max: 1440 }}
                                helperText="Mínimo: 5 min, Máximo: 1440 min (24h)"
                            />

                            <TextField
                                label="Domínios por execução"
                                type="number"
                                value={config.enrichmentScheduler?.batchSize || 10}
                                onChange={(e) => setConfig({
                                    ...config,
                                    enrichmentScheduler: {
                                        ...config.enrichmentScheduler,
                                        batchSize: parseInt(e.target.value)
                                    }
                                })}
                                inputProps={{ min: 1, max: 100 }}
                                helperText="Quantidade de domínios processados por vez"
                            />
                        </Box>

                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 3 }}>
                            <TextField
                                label="Horário início (hora)"
                                type="number"
                                value={config.enrichmentScheduler?.runHoursStart || 8}
                                onChange={(e) => setConfig({
                                    ...config,
                                    enrichmentScheduler: {
                                        ...config.enrichmentScheduler,
                                        runHoursStart: parseInt(e.target.value)
                                    }
                                })}
                                inputProps={{ min: 0, max: 23 }}
                                helperText="Hora de início (0-23)"
                            />

                            <TextField
                                label="Horário fim (hora)"
                                type="number"
                                value={config.enrichmentScheduler?.runHoursEnd || 22}
                                onChange={(e) => setConfig({
                                    ...config,
                                    enrichmentScheduler: {
                                        ...config.enrichmentScheduler,
                                        runHoursEnd: parseInt(e.target.value)
                                    }
                                })}
                                inputProps={{ min: 0, max: 23 }}
                                helperText="Hora de término (0-23)"
                            />
                        </Box>

                        <FormControlLabel
                            control={
                                <Switch
                                    checked={config.enrichmentScheduler?.autoOpenBrowser || false}
                                    onChange={(e) => setConfig({
                                        ...config,
                                        enrichmentScheduler: {
                                            ...config.enrichmentScheduler,
                                            autoOpenBrowser: e.target.checked
                                        }
                                    })}
                                />
                            }
                            label="Auto-abrir browser do SimilarWeb se necessário"
                        />

                        <Typography variant="subtitle1" gutterBottom sx={{ mt: 3 }}>
                            Prioridades a Processar
                        </Typography>

                        <FormGroup>
                            {[
                                { value: 1, label: 'P1 - Urgente', desc: 'Sem dados reais, precisa atualização imediata' },
                                { value: 2, label: 'P2 - Alta', desc: 'Tier 1 (premium), atualização mensal' },
                                { value: 3, label: 'P3 - Normal', desc: 'Tier 2 (médio), atualização trimestral' },
                                { value: 4, label: 'P4 - Baixa', desc: 'Tier 3 (básico), atualização semestral' },
                                { value: 5, label: 'P5 - Mínima', desc: 'Pouco usado, atualização anual' }
                            ].map(({ value, label, desc }) => (
                                <FormControlLabel
                                    key={value}
                                    control={
                                        <Checkbox
                                            checked={config.enrichmentScheduler?.priorityFilter?.includes(value) || false}
                                            onChange={(e) => {
                                                const currentFilters = config.enrichmentScheduler?.priorityFilter || [];
                                                const newFilters = e.target.checked
                                                    ? [...currentFilters, value]
                                                    : currentFilters.filter(p => p !== value);
                                                setConfig({
                                                    ...config,
                                                    enrichmentScheduler: {
                                                        ...config.enrichmentScheduler,
                                                        priorityFilter: newFilters.sort()
                                                    }
                                                });
                                            }}
                                        />
                                    }
                                    label={
                                        <Box>
                                            <Typography variant="body2" component="span" sx={{ fontWeight: 500 }}>
                                                {label}
                                            </Typography>
                                            <Typography variant="caption" component="span" sx={{ ml: 1, color: 'text.secondary' }}>
                                                {desc}
                                            </Typography>
                                        </Box>
                                    }
                                />
                            ))}
                        </FormGroup>

                        {/* Status em Tempo Real */}
                        {schedulerStatus && (
                            <Box sx={{ mt: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                                <Typography variant="subtitle1" gutterBottom>
                                    📊 Status Atual
                                </Typography>
                                <Divider sx={{ mb: 2 }} />

                                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2 }}>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">Status</Typography>
                                        <Typography variant="body1">
                                            {schedulerStatus.isRunning ? '🔄 Executando' :
                                                schedulerStatus.status === 'idle' ? '⏸️ Aguardando' :
                                                    schedulerStatus.status === 'error' ? '❌ Erro' : schedulerStatus.status}
                                        </Typography>
                                    </Box>

                                    <Box>
                                        <Typography variant="caption" color="text.secondary">Última Execução</Typography>
                                        <Typography variant="body1">
                                            {schedulerStatus.lastRun ? new Date(schedulerStatus.lastRun).toLocaleString('pt-BR') : 'Nunca'}
                                        </Typography>
                                    </Box>

                                    <Box>
                                        <Typography variant="caption" color="text.secondary">Próxima Execução</Typography>
                                        <Typography variant="body1">
                                            {schedulerStatus.nextRun ? new Date(schedulerStatus.nextRun).toLocaleString('pt-BR') : 'N/A'}
                                        </Typography>
                                    </Box>
                                </Box>

                                {schedulerStatus.stats && (
                                    <>
                                        <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Estatísticas</Typography>
                                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 2 }}>
                                            <Box>
                                                <Typography variant="caption" color="text.secondary">Total Processado</Typography>
                                                <Typography variant="h6">{schedulerStatus.stats.totalProcessed}</Typography>
                                            </Box>
                                            <Box>
                                                <Typography variant="caption" color="text.secondary">Sucessos</Typography>
                                                <Typography variant="h6" color="success.main">{schedulerStatus.stats.totalSuccess}</Typography>
                                            </Box>
                                            <Box>
                                                <Typography variant="caption" color="text.secondary">Falhas</Typography>
                                                <Typography variant="h6" color="error.main">{schedulerStatus.stats.totalFailed}</Typography>
                                            </Box>
                                            <Box>
                                                <Typography variant="caption" color="text.secondary">Último Batch</Typography>
                                                <Typography variant="h6">{schedulerStatus.stats.lastBatchSize} ({schedulerStatus.stats.lastBatchDuration}s)</Typography>
                                            </Box>
                                        </Box>
                                    </>
                                )}
                            </Box>
                        )}

                        {/* Ações */}
                        <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                            <Button
                                variant="outlined"
                                color="primary"
                                onClick={handleRunScheduler}
                                disabled={schedulerRunning || schedulerStatus?.isRunning}
                            >
                                {schedulerRunning || schedulerStatus?.isRunning ? <CircularProgress size={24} /> : '▶️ Executar Agora'}
                            </Button>

                            <Button
                                variant="outlined"
                                color="secondary"
                                onClick={handleResetStats}
                                disabled={schedulerLoading}
                            >
                                {schedulerLoading ? <CircularProgress size={24} /> : '🔄 Resetar Estatísticas'}
                            </Button>

                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleSaveScheduler}
                                disabled={saving}
                            >
                                {saving ? <CircularProgress size={24} /> : '💾 Salvar Configurações do Scheduler'}
                            </Button>
                        </Box>
                    </>
                )}
            </Paper>

            {/* CONFIGURAÇÕES DE SCRAPING */}
            <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                    🔧 Configurações de Scraping
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <Alert severity="info" sx={{ mb: 3 }}>
                    Configure qual scraper usar para extração de conteúdo de sites Online.
                    As redes sociais (YouTube, Instagram, Twitter, etc.) usam scrapers dedicados.
                </Alert>

                <FormControl component="fieldset" sx={{ mb: 3 }}>
                    <FormLabel component="legend">Scraper Padrão para Sites</FormLabel>
                    <RadioGroup
                        value={config.scraping?.defaultScraper || 'StealthScraper'}
                        onChange={(e) => setConfig({
                            ...config,
                            scraping: {
                                ...config.scraping,
                                defaultScraper: e.target.value
                            }
                        })}
                    >
                        <FormControlLabel
                            value="StealthScraper"
                            control={<Radio />}
                            label={
                                <Box>
                                    <Typography variant="body2" component="span" sx={{ fontWeight: 500 }}>
                                        StealthScraper (Recomendado)
                                    </Typography>
                                    <Typography variant="caption" component="span" sx={{ ml: 1, color: 'text.secondary' }}>
                                        Browser headless com 67 seletores, executa JavaScript
                                    </Typography>
                                </Box>
                            }
                        />
                        <FormControlLabel
                            value="GeminiVision"
                            control={<Radio />}
                            label={
                                <Box>
                                    <Typography variant="body2" component="span" sx={{ fontWeight: 500 }}>
                                        GeminiVision (IA)
                                    </Typography>
                                    <Typography variant="caption" component="span" sx={{ ml: 1, color: 'text.secondary' }}>
                                        Análise visual com IA, alta precisão (~95%), requer API key
                                    </Typography>
                                </Box>
                            }
                        />
                        <FormControlLabel
                            value="SimpleScraper"
                            control={<Radio />}
                            label={
                                <Box>
                                    <Typography variant="body2" component="span" sx={{ fontWeight: 500 }}>
                                        SimpleScraper
                                    </Typography>
                                    <Typography variant="caption" component="span" sx={{ ml: 1, color: 'text.secondary' }}>
                                        HTTP básico sem JavaScript, mais rápido mas menos confiável
                                    </Typography>
                                </Box>
                            }
                        />
                        <FormControlLabel
                            value="SpiderScraper"
                            control={<Radio />}
                            label={
                                <Box>
                                    <Typography variant="body2" component="span" sx={{ fontWeight: 500 }}>
                                        SpiderScraper
                                    </Typography>
                                    <Typography variant="caption" component="span" sx={{ ml: 1, color: 'text.secondary' }}>
                                        Usa seletores específicos do banco de dados (SiteConfig)
                                    </Typography>
                                </Box>
                            }
                        />
                    </RadioGroup>
                </FormControl>

                <FormControlLabel
                    control={
                        <Switch
                            checked={config.scraping?.tryGeminiFirst || false}
                            onChange={(e) => setConfig({
                                ...config,
                                scraping: {
                                    ...config.scraping,
                                    tryGeminiFirst: e.target.checked
                                }
                            })}
                        />
                    }
                    label="Tentar GeminiVision primeiro (antes do scraper padrão)"
                />

                <Box sx={{ mt: 3, mb: 2 }}>
                    <TextField
                        label="Timeout do Scraper (segundos)"
                        type="number"
                        value={config.scraping?.scraperTimeout || 45}
                        onChange={(e) => setConfig({
                            ...config,
                            scraping: {
                                ...config.scraping,
                                scraperTimeout: parseInt(e.target.value)
                            }
                        })}
                        inputProps={{ min: 5, max: 120 }}
                        helperText="Tempo máximo de espera para cada scraper (5-120 segundos)"
                        sx={{ width: 300 }}
                    />
                </Box>

                <Typography variant="subtitle1" gutterBottom sx={{ mt: 3 }}>
                    Scrapers Habilitados
                </Typography>

                <FormGroup>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={config.scraping?.geminiVision?.enabled ?? true}
                                onChange={(e) => setConfig({
                                    ...config,
                                    scraping: {
                                        ...config.scraping,
                                        geminiVision: {
                                            ...config.scraping?.geminiVision,
                                            enabled: e.target.checked
                                        }
                                    }
                                })}
                            />
                        }
                        label="GeminiVision (requer GEMINI_API_KEY)"
                    />

                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={config.scraping?.stealthScraper?.enabled ?? true}
                                onChange={(e) => setConfig({
                                    ...config,
                                    scraping: {
                                        ...config.scraping,
                                        stealthScraper: {
                                            ...config.scraping?.stealthScraper,
                                            enabled: e.target.checked
                                        }
                                    }
                                })}
                            />
                        }
                        label="StealthScraper (browser headless)"
                    />

                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={config.scraping?.simpleScraper?.enabled ?? true}
                                onChange={(e) => setConfig({
                                    ...config,
                                    scraping: {
                                        ...config.scraping,
                                        simpleScraper: {
                                            ...config.scraping?.simpleScraper,
                                            enabled: e.target.checked
                                        }
                                    }
                                })}
                            />
                        }
                        label="SimpleScraper (HTTP fallback)"
                    />
                </FormGroup>

                <FormControlLabel
                    sx={{ mt: 2 }}
                    control={
                        <Switch
                            checked={config.scraping?.verboseLogging || false}
                            onChange={(e) => setConfig({
                                ...config,
                                scraping: {
                                    ...config.scraping,
                                    verboseLogging: e.target.checked
                                }
                            })}
                        />
                    }
                    label="Logs detalhados (debug)"
                />

                {/* Estatísticas de Scraping */}
                {config.scraping?.stats && (config.scraping.stats.totalScraped > 0) && (
                    <Box sx={{ mt: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                        <Typography variant="subtitle1" gutterBottom>
                            📊 Estatísticas de Scraping
                        </Typography>
                        <Divider sx={{ mb: 2 }} />

                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2 }}>
                            <Box>
                                <Typography variant="caption" color="text.secondary">Total Scrapings</Typography>
                                <Typography variant="h6">{config.scraping.stats.totalScraped}</Typography>
                            </Box>
                            <Box>
                                <Typography variant="caption" color="text.secondary">Sucessos</Typography>
                                <Typography variant="h6" color="success.main">{config.scraping.stats.successCount}</Typography>
                            </Box>
                            <Box>
                                <Typography variant="caption" color="text.secondary">Falhas</Typography>
                                <Typography variant="h6" color="error.main">{config.scraping.stats.failCount}</Typography>
                            </Box>
                        </Box>
                    </Box>
                )}
            </Paper>

            {/* CONFIGURAÇÕES DE CONTATO / SUPORTE */}
            <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                    📞 Contato & Suporte
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <Alert severity="info" sx={{ mb: 3 }}>
                    Esses dados são usados no modal de upsell quando um cliente atinge o limite mensal de publicações.
                </Alert>

                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                    <TextField
                        label="WhatsApp (apenas dígitos, com DDI)"
                        value={config.contact?.whatsapp || ''}
                        onChange={(e) => setConfig({
                            ...config,
                            contact: {
                                ...config.contact,
                                whatsapp: e.target.value.replace(/\D/g, '')
                            }
                        })}
                        helperText="Ex: 5511980464551 · aparece no botão 'Falar com Mensure'"
                        inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                    />

                    <TextField
                        label="E-mail de contato"
                        type="email"
                        value={config.contact?.email || ''}
                        onChange={(e) => setConfig({
                            ...config,
                            contact: {
                                ...config.contact,
                                email: e.target.value
                            }
                        })}
                        helperText="E-mail de suporte comercial"
                    />
                </Box>
            </Paper>

            <Box display="flex" justifyContent="flex-end">
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSave}
                    disabled={saving}
                >
                    {saving ? <CircularProgress size={24} /> : 'Salvar Configurações'}
                </Button>
            </Box>
        </Box>
    );
};

export default SystemConfigPanel;
